import React from 'react';
import { CircleCheckBig } from 'lucide-react';
import { getCardLevelThumbnail } from '~/util';
import { LevelStatus } from '~/util/types/levels';

export interface LevelHistoryEntry {
    name: string;
    position: number | null;
    percentage?: number;
    level_id: number;
    status?: LevelStatus;
}

interface LevelHistoryProps {
    levelHistory: LevelHistoryEntry[];
    title?: string;
    emptyMessage?: string;
    showPercentage?: boolean;
    showCheckIcon?: boolean;
}

const LevelHistory: React.FC<LevelHistoryProps> = ({
    levelHistory,
    title = 'Level History',
    emptyMessage = 'No levels have been completed yet.',
    showPercentage = true,
    showCheckIcon = true,
}) => {
    return (
        <div className="w-full lg:w-1/2 clamp-[p-2-4-clamp] bg-background-dark rounded-xl shadow-md mx-auto">
            <h2 className="clamp-[text-2xl-3xl-clamp] font-bold text-center  text-foreground">
                {title}
            </h2>

            {/* the fallback message when no levels have been listed */}
            {levelHistory.length === 0 ? (
                <p className="text-center text-secondary-light italic text-lg">
                    {emptyMessage}
                </p>
            ) : (
                <div className="space-y-2">
                    {levelHistory.map((entry) => (
                        <div
                            key={`${entry.level_id}-${entry.position}`}
                            className="relative rounded-xl overflow-hidden clamp-[p-2-6-clamp] bg-black/20 "
                        >
                            {/* using the level thumbnail as background */}
                            <img
                                src={getCardLevelThumbnail(entry.level_id)} // Pass the level_id directly as a number
                                alt={`Thumbnail for level ${entry.name}`}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="relative z-10 flex justify-between items-center h-full px-4">
                                {/* the level details */}
                                <div className="flex flex-col">
                                    <h3 className="clamp-[text-sm-xl-clamp]! font-bold text-shadow-3">
                                        {getLevelHistoryEntryName(entry)}
                                    </h3>
                                    {showPercentage &&
                                        entry.percentage != null && (
                                            <p className="clamp-[text-xs-lg-clamp]! text-shadow-3">
                                                {`Completed: ${entry.percentage}%`}
                                            </p>
                                        )}
                                </div>
                                {/* the checkmark with too many shaodws ngl but looks cool */}
                                {showCheckIcon && (
                                    <div className="relative inline-block w-max transition-transform align-middle">
                                        <div
                                            className="absolute text-black blur-xs z-0"
                                            aria-hidden="true"
                                        >
                                            <CircleCheckBig size={44} />
                                        </div>
                                        <div className="relative z-10 text-green-400">
                                            <CircleCheckBig size={40} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

function getLevelHistoryEntryName(entry: LevelHistoryEntry) {
    if (entry.status && entry.status !== 'MainList') {
        return `${entry.status} - ${entry.name}`;
    }

    return entry.position != null
        ? `#${entry.position} - ${entry.name}`
        : entry.name;
}

export default LevelHistory;
